import React, { useMemo, useState } from "react";
import pluswhite from "assets/images/icon/plus-white.png";
import filtersearch from "assets/images/icon/filter-search.png";
import Table from "./Table";
import Input from "components/Input";
import AddPayScheduleModal from "pages/AddPayScheduleModal";
import PayScheduleDetailsModal from "pages/PayScheduleDetailsModal";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getPaySchedules, deletePaySchedule } from "services/paySchedules";

const headers = [
    { headerName: "Name", key: "name" },
    { headerName: "Frequency", key: "frequency" },
    { headerName: "Active Status", key: "active_status" },
    { headerName: "First Payday", key: "firstPayday" },
    { headerName: "", key: "action" },
];

const ManagePaySchedules = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selected, setSelected] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [defaultEditing, setDefaultEditing] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const queryClient = useQueryClient();

    const { data: schedulesRaw = [], isLoading } = useQuery({ queryKey: ['pay-schedules'], queryFn: getPaySchedules });

    const deleteMutation = useMutation({
        mutationFn: (id) => deletePaySchedule(id),
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['pay-schedules'] }); setDeleteTarget(null); },
    });

    const tableData = useMemo(() => schedulesRaw
        .filter(s => !searchTerm || (s.name || '').toLowerCase().includes(searchTerm.toLowerCase()))
        .map(s => ({
            id: s.id,
            name: s.name || s.frequency,
            frequency: s.frequency,
            active_status: s.active ? 'Active' : 'Inactive',
            firstPayday: s.anchorPayDate ? new Date(s.anchorPayDate).toLocaleDateString() : '—',
        })),
        [schedulesRaw, searchTerm]);

    const handleEdit = (row) => {
        setSelected(schedulesRaw.find(s => s.id === row.id) || row);
        setDefaultEditing(true);
        setDetailsOpen(true);
    };

    const handleView = (row) => {
        setSelected(schedulesRaw.find(s => s.id === row.id) || row);
        setDefaultEditing(false);
        setDetailsOpen(true);
    };

    const handleDelete = (row) => setDeleteTarget(schedulesRaw.find(s => s.id === row.id) || row);

    return (
        <>
            <div>
                <div className='d-flex justify-content-between align-content-end border-bottom pb-4'>
                    <div>
                        <h2 className='sub-title'>Manage Pay Schedules</h2>
                        <button className='button-orange py-2 mt-3 rounded-pill d-flex justify-content-center align-items-center gap-2' onClick={() => setIsOpen(true)}>
                            <img src={pluswhite} alt="add" />Add Pay Schedule
                        </button>
                    </div>
                    <div className='align-content-end'>
                        <Input placeholder='Search by Name' startIcon={filtersearch} style={{ width: '300px' }} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                    </div>
                </div>
                <div>
                    {isLoading ? <p className="text-center mt-4">Loading pay schedules...</p> : (
                        <Table headers={headers} data={tableData} showPagination={true} handleEdit={handleEdit} handleView={handleView} handleDelete={handleDelete} />
                    )}
                </div>
            </div>
            <AddPayScheduleModal open={isOpen} onClose={() => { setIsOpen(false); queryClient.invalidateQueries({ queryKey: ['pay-schedules'] }); }} />
            <PayScheduleDetailsModal
                schedule={selected}
                open={detailsOpen}
                defaultEditing={defaultEditing}
                onClose={() => { setDetailsOpen(false); setSelected(null); }}
            />
            {deleteTarget && (
                <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.4)' }}>
                    <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
                        <div className="modal-content p-4">
                            <h5 className="mb-2">Delete Pay Schedule</h5>
                            <p className="text-muted mb-4">Are you sure you want to delete <strong>{deleteTarget.name || deleteTarget.frequency}</strong>? This action cannot be undone.</p>
                            {deleteMutation.isError && <p className="text-danger mb-3">Failed to delete. Please try again.</p>}
                            <div className="d-flex gap-2 justify-content-end">
                                <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setDeleteTarget(null)} disabled={deleteMutation.isPending}>Cancel</button>
                                <button className="btn btn-danger rounded-pill px-4" onClick={() => deleteMutation.mutate(deleteTarget.id)} disabled={deleteMutation.isPending}>
                                    {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ManagePaySchedules;
